/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * CLIENT COPY (fault 1, walk w4384eecc, 11/10/2026). What the client reads must
 * carry no dash and must be written to the client, never about them. In that
 * walk the client's checklist and the "Noted for" email carried a document gap
 * the model wrote for the officer, word for word: "The Northfold Bank statements
 * the client says he holds for the two GBP 50,000 transfers — ... These were
 * offered in the conversation but not provided; they are the direct evidence for
 * the founding capital narrative." The draft request's own lines kept "top-end"
 * and "follow-up", since noteText (built for case notes) leaves word hyphens on
 * purpose.
 *
 * clientText: noteText, plus one rule: a hyphen between two lower case words (or
 * a figure and a lower case word) becomes a space ("follow up", "top end",
 * "12 month"). After a short list of prefixes the hyphen is dropped instead
 * ("re-sent" becomes "resent"). A capitalised name ("Smith-Jones"), a range of
 * figures, an email address and a web address are left as they are.
 *
 * clientGapLabel: the client form of a document gap. "the client says he holds"
 * becomes "you mentioned you hold", "the client's" becomes "your", and trailing
 * sentences of the file's own reasoning are dropped. A label it cannot make
 * client facing becomes "Please provide: <document>" from the gap's document
 * type. Deterministic and narrow; the officer keeps the full label.
 */
import { noteText } from './case-note-text.js';
import { rawSectionLineKey, sectionClientAsk } from './board-sections.js';

// The prompt lines (fault 1, part C). Generation paths: the interview report,
// the officer's regenerate, the documents first fold.
export const GAP_PLAIN_RULE = 'DOCUMENT GAPS NAME THE DOCUMENT PLAINLY: each documentGaps entry names the document itself and why it is needed, written so the client could read it. Never say who said what (never "the client says" or "he mentioned"), never add the file\'s own reasoning (never "these were offered in the conversation but not provided"), and use no dashes.';
export const REQUEST_TO_CLIENT_RULE = 'EVERY REQUEST IS WRITTEN TO THE CLIENT, NEVER ABOUT THEM: say "you" and "your", never "the client", "the applicant", "he" or "she" for the client. Use no dashes, and no hyphen joining two words (write follow up and top end as two words).';

const JOIN_PREFIXES = new Set(['re', 'pre', 'co', 'non']);
const KEEP_TOKEN = /@|:\/\/|^www\./i;

function hyphenWord(token) {
  if (KEEP_TOKEN.test(token)) return token;
  return token.replace(/(^|[^A-Za-z0-9])([a-z0-9]+)-(?=[a-z])/g, (m, pre, left) => {
    if (JOIN_PREFIXES.has(left)) return pre + left;
    return pre + left + ' ';
  }).replace(/([a-z0-9])-(?=[a-z])/g, '$1 ');
}

/** Client facing text: no dash, and no hyphen joining lower case words. */
export function clientText(s) {
  if (typeof s !== 'string') return s;
  const cleaned = noteText(s);
  if (!/-/.test(cleaned)) return cleaned;
  return cleaned.split(/(\s+)/).map(t => (/\s/.test(t) ? t : hyphenWord(t))).join('');
}

export function clientTextLines(lines) {
  return Array.isArray(lines) ? lines.map(l => (typeof l === 'string' ? clientText(l) : l)) : lines;
}

// Third person verbs after "the client says he/she/they": the form for "you".
const VERB_FOR_YOU = { has: 'have', is: 'are', was: 'were', does: 'do', holds: 'hold', keeps: 'keep', owns: 'own', can: 'can', will: 'will', would: 'would', could: 'could', received: 'received', sent: 'sent', made: 'made', paid: 'paid', used: 'used' };
const youVerb = (v) => {
  const k = String(v || '').toLowerCase();
  if (VERB_FOR_YOU[k]) return VERB_FOR_YOU[k];
  if (/ies$/.test(k)) return k.replace(/ies$/, 'y');
  if (/(ss|us)$/.test(k)) return k;
  if (/(ch|sh|x|o)es$/.test(k)) return k.replace(/es$/, '');
  if (/s$/.test(k)) return k.replace(/s$/, '');
  return k;
};
// Sentences that are the file's own reasoning, not the ask.
const REASONING = /\b(?:offered in the conversation|were offered|was offered|not (?:yet )?provided|direct evidence|the evidence for|evidence for the|narrative|for the file|on file to|corroborat\w*|needed to (?:verify|evidence|confirm|support)|required to (?:verify|evidence|confirm|support)|to support (?:the|his|her|their) (?:account|claim|statement)|this (?:is|would be) the|they are the|it is the)\b/i;
// What still reads as written about the client, or for the compliance team.
const NOT_CLIENT_FACING = /\bthe (?:client|applicant|customer)\b|\b(?:his|himself|herself)\b|\b(?:he|she) (?:says|said|claims|claimed|mentioned|stated)\b|\bMLRO\b|\bred flag|\brisk (?:tier|rating|score)\b|\bscreening (?:hit|match)\b|\bsuspicio/i;

const DOC_NAMES = {
  constitutional: 'the constitutional documents (certificate of incorporation, memorandum and articles)',
  register: 'the register of members and directors',
  structureChart: 'a group structure chart',
  bank: 'the bank statements',
  financials: 'the latest financial statements',
  identity: 'an identity document',
  activityDoc: 'a sample invoice or customer contract',
  licence: 'the licence or registration certificate',
  addressDoc: 'a proof of address',
};

function sentencesOf(s) {
  return String(s || '').replace(/\s+/g, ' ').trim().split(/(?<=[.;])\s+(?=[A-Z])/).map(x => x.trim()).filter(Boolean);
}

/**
 * The client form of a document gap label. docType is the classification
 * docTypeOf gives (optional; worked out from the label when absent).
 * Returns the client label (always client text).
 */
export function clientGapLabel(label, docType) {
  const raw = String(label || '').trim();
  if (!raw) return raw;
  // Walk wb0c3f810: a stored "<section key>: <finding>" line (written before
  // the assembly check filed its findings as questions) is the section's
  // plain question, never the key and never the finding.
  const sectionKey = rawSectionLineKey(raw);
  if (sectionKey) return clientText(sectionClientAsk(sectionKey));
  let s = noteText(raw);
  // Pronoun phrasing first, then possessives, then the bare noun.
  s = s.replace(/\bthe (?:client|applicant)(?: has)? (?:says|said|mentions|mentioned|states|stated|indicates|indicated|confirms|confirmed|explains|explained)(?: that)? (?:he|she|they) (\w+)/gi, (m, v) => 'you mentioned you ' + youVerb(v));
  s = s.replace(/\bthe (?:client|applicant)(?: has)? (?:says|said|mentions|mentioned|states|stated|indicates|indicated|confirms|confirmed|explains|explained)(?: that)?\b/gi, 'you mentioned');
  s = s.replace(/\bthe (?:client|applicant)['’]s\b/gi, 'your');
  s = s.replace(/\bhis or her\b|\bhis\/her\b/gi, 'your');
  // Once the label speaks to the client, the client's own "his" (and "her"
  // before a possession) is "your", and "himself" or "herself" is "yourself".
  if (/\byou(?:r)?\b/i.test(s) && s !== noteText(raw)) {
    s = s.replace(/\bhis\b/g, 'your').replace(/\b(?:himself|herself)\b/g, 'yourself')
      .replace(/\bher (?=(?:own|personal|private|savings|bank|account|accounts|shares|holding|holdings|company|business|salary|income|funds|wealth)\b)/g, 'your ');
  }
  // The file's own reasoning, after the ask, is dropped.
  const parts = sentencesOf(s);
  const kept = parts.length > 1 ? [parts[0], ...parts.slice(1).filter(p => !REASONING.test(p))] : parts;
  s = kept.join(' ').replace(/[;,]\s*$/, '').trim();
  if (s && !/[.?!]$/.test(s)) s += '.';
  s = clientText(s.charAt(0).toUpperCase() + s.slice(1));
  if (!NOT_CLIENT_FACING.test(s)) return s;
  const name = DOC_NAMES[docType] || null;
  if (name) return clientText('Please provide ' + name + '.');
  // No document type: the ask's first clause, turned to the client.
  const head = noteText(raw).split(/[.;:(]|,\s/)[0]
    .replace(/\bthe (?:client|applicant)['\u2019]s\b/gi, 'your').replace(/\b(?:his|her)\b/g, 'your')
    .replace(/\bthe (?:client|applicant)\b/gi, 'you').trim();
  const ask = clientText('Please provide: ' + head.charAt(0).toLowerCase() + head.slice(1) + '.');
  // A head that is a bare identifier (camelCase, snake_case, a dotted path) is
  // a field name, never an ask.
  const identifier = /^[a-z]+(?:[A-Z][a-z0-9]*)+$|^[a-z0-9]+(?:[_.][a-z0-9]+)+$/i.test(head) && !/\s/.test(head);
  return head && !identifier && !NOT_CLIENT_FACING.test(ask) ? ask : 'Please provide the document the compliance team asked for.';
}

/** The label a client surface shows for a request item. */
// The client form is used only while the label is the one it was made from
// (clientLabelFor): once an officer rewords the item, their wording is what the
// client reads. A document gap item stored before the client form existed gets
// it worked out here.
export function clientItemLabel(item) {
  if (!item || typeof item !== 'object') return '';
  const label = String(item.label || '');
  if (typeof item.clientLabel === 'string' && item.clientLabel.trim() && item.clientLabelFor === label) return clientText(item.clientLabel);
  if (item.source === 'documentGaps' && item.clientLabelFor === undefined) return clientGapLabel(label);
  return clientText(label);
}
